import React, { useState, useMemo } from "react";
import { useTheme } from "../../context/ThemeContext";
import {
  getPrimaryText,
  getSecondaryText,
  getLightBorder,
  getMutedText,
} from "../../utils/themeUtils";
import { ChevronLeft, ChevronRight, Calendar, Clock } from "lucide-react";
import { Button, Card } from "../ui";

// --- Types ---

interface ProgramSlot {
  id: string;
  program_id: string;
  slot_id: string;
  days?: string[] | null;
  times?: (string | null)[] | null;
  duration_hours?: number | null;
}

interface Program {
  id: string;
  label: string;
}

interface Enrollment {
  id: string;
  program_slot: ProgramSlot | null;
  program: Program | null;
}

interface Athlete {
  id: string;
  first_name: string;
  last_name: string;
  enrollments: Enrollment[];
}

interface ScheduleBlock {
  athleteName: string;
  athleteColor: string;
  programLabel: string;
  startTime: string;
  endTime: string;
  startMinutes: number;
}

interface Props {
  athletes: Athlete[];
}

type ViewMode = "week" | "month";

// --- Constants ---

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// Distinct athlete colors (tailwind-friendly hex)
const ATHLETE_COLORS = [
  "#3B82F6", // blue-500
  "#10B981", // emerald-500
  "#F59E0B", // amber-500
  "#EF4444", // red-500
  "#8B5CF6", // violet-500
  "#EC4899", // pink-500
  "#06B6D4", // cyan-500
  "#F97316", // orange-500
];

// --- Helpers ---

/**
 * Parse a time string like "18:00", "6:00 PM", or "6:00 PM - 7:00 PM"
 * Returns { startTime: "6:00 PM", endTime: "7:00 PM" | null, startMinutes: number }
 */
function parseTimeEntry(
  raw: string,
  durationHours?: number | null
): { startTime: string; endTime: string; startMinutes: number } | null {
  if (!raw || raw === "x") return null;

  // Check for range format: "6:00 PM - 7:00 PM"
  const rangeParts = raw.split(/\s*-\s*/);

  let startStr = rangeParts[0].trim();
  let endStr = rangeParts.length > 1 ? rangeParts[rangeParts.length - 1].trim() : null;

  const startMinutes = timeToMinutes(startStr);
  if (startMinutes === null) return null;

  // Calculate end time
  if (!endStr && durationHours) {
    const endMinutes = startMinutes + Math.round(durationHours * 60);
    endStr = minutesToTimeStr(endMinutes);
  }

  return {
    startTime: formatTimeDisplay(startStr),
    endTime: endStr ? formatTimeDisplay(endStr) : formatTimeDisplay(startStr),
    startMinutes,
  };
}

/** Convert a time string to total minutes from midnight */
function timeToMinutes(t: string): number | null {
  // Handle "6:00 PM" or "18:00"
  const cleaned = t.trim().toUpperCase();

  // 12-hour format
  const match12 = cleaned.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (match12) {
    let hours = parseInt(match12[1], 10);
    const mins = parseInt(match12[2], 10);
    const period = match12[3];
    if (period === "AM" && hours === 12) hours = 0;
    if (period === "PM" && hours !== 12) hours += 12;
    return hours * 60 + mins;
  }

  // 24-hour format
  const match24 = cleaned.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    const hours = parseInt(match24[1], 10);
    const mins = parseInt(match24[2], 10);
    return hours * 60 + mins;
  }

  return null;
}

/** Convert minutes from midnight to a display string like "6:00 PM" */
function minutesToTimeStr(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const period = h >= 12 ? "PM" : "AM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${String(m).padStart(2, "0")} ${period}`;
}

/** Normalize any time string to "h:mm AM/PM" display format */
function formatTimeDisplay(t: string): string {
  const mins = timeToMinutes(t);
  if (mins === null) return t;
  return minutesToTimeStr(mins);
}

/**
 * Get the Monday of the week containing `date`.
 * Uses ISO week (Monday = start).
 */
function getWeekMonday(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay(); // 0=Sun, 1=Mon...
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

/** Format a date as "Feb 20" */
function formatShortDate(date: Date): string {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// --- Component ---

const MemberScheduleCalendar: React.FC<Props> = ({ athletes }) => {
  const { isDarkMode } = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  const [currentDate, setCurrentDate] = useState(new Date());

  // Brand colors via --color-primary CSS var. See CLAUDE.md.

  // Assign a color to each athlete
  const athleteColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    athletes.forEach((a, i) => {
      map[a.id] = ATHLETE_COLORS[i % ATHLETE_COLORS.length];
    });
    return map;
  }, [athletes]);

  // Build schedule blocks per day-of-week (0=Mon..6=Sun) from all athletes' enrollments
  const scheduleByDay = useMemo(() => {
    const byDay: Record<number, ScheduleBlock[]> = {};
    for (let i = 0; i < 7; i++) byDay[i] = [];

    athletes.forEach((athlete) => {
      const color = athleteColorMap[athlete.id];
      const name = `${athlete.first_name} ${athlete.last_name}`;

      athlete.enrollments?.forEach((enrollment) => {
        const slot = enrollment.program_slot;
        const program = enrollment.program;
        if (!slot?.times || !program) return;

        const times = slot.times;
        // times is a 7-element array indexed 0=Mon..6=Sun
        times.forEach((timeEntry, dayIdx) => {
          if (!timeEntry || timeEntry === "x") return;
          const parsed = parseTimeEntry(timeEntry, slot.duration_hours);
          if (!parsed) return;

          byDay[dayIdx].push({
            athleteName: name,
            athleteColor: color,
            programLabel: program.label,
            startTime: parsed.startTime,
            endTime: parsed.endTime,
            startMinutes: parsed.startMinutes,
          });
        });
      });
    });

    // Sort each day's blocks by start time
    for (let i = 0; i < 7; i++) {
      byDay[i].sort((a, b) => a.startMinutes - b.startMinutes);
    }

    return byDay;
  }, [athletes, athleteColorMap]);

  // --- Navigation ---

  const weekMonday = getWeekMonday(currentDate);

  const goToPrevious = () => {
    if (viewMode === "week") {
      const d = new Date(currentDate);
      d.setDate(d.getDate() - 7);
      setCurrentDate(d);
    } else {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
      );
    }
  };

  const goToNext = () => {
    if (viewMode === "week") {
      const d = new Date(currentDate);
      d.setDate(d.getDate() + 7);
      setCurrentDate(d);
    } else {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
      );
    }
  };

  const goToToday = () => setCurrentDate(new Date());

  // --- Week dates ---

  const weekDates = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekMonday);
      d.setDate(weekMonday.getDate() + i);
      return d;
    });
  }, [weekMonday]);

  const todayStr = new Date().toDateString();

  // --- Month helpers ---

  const monthYear = `${currentDate.toLocaleDateString("en-US", { month: "long" })} ${currentDate.getFullYear()}`;
  const weekRange = `${formatShortDate(weekDates[0])} – ${formatShortDate(weekDates[6])}, ${weekDates[6].getFullYear()}`;

  // Generate month grid
  const monthDays = useMemo(() => {
    if (viewMode !== "month") return [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // getDay() is 0=Sun, convert to 0=Mon
    const firstDayRaw = new Date(year, month, 1).getDay();
    const firstDay = firstDayRaw === 0 ? 6 : firstDayRaw - 1;

    const days: { day: number | null; date: Date | null; dayOfWeekIdx: number }[] = [];

    // Padding before first day
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, date: null, dayOfWeekIdx: i });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dow = date.getDay();
      const dayOfWeekIdx = dow === 0 ? 6 : dow - 1; // convert to 0=Mon
      days.push({ day: d, date, dayOfWeekIdx });
    }

    return days;
  }, [viewMode, currentDate]);

  // Month view: expanded day detail
  const [expandedDay, setExpandedDay] = useState<number | null>(null);

  // --- Render schedule block ---
  const renderBlock = (block: ScheduleBlock, compact = false) => (
    <div
      key={`${block.programLabel}-${block.athleteName}-${block.startTime}`}
      className={`rounded-md border-l-3 ${compact ? "px-1.5 py-0.5" : "px-2 py-1.5"}`}
      style={{
        borderLeftColor: block.athleteColor,
        borderLeftWidth: "3px",
        backgroundColor: isDarkMode
          ? `${block.athleteColor}15`
          : `${block.athleteColor}10`,
      }}
    >
      <div
        className={`font-medium ${compact ? "text-xs" : "text-sm"} ${getPrimaryText(isDarkMode)} truncate`}
      >
        {block.programLabel}
      </div>
      <div
        className={`flex items-center gap-1 ${compact ? "text-[10px]" : "text-xs"} ${getMutedText(isDarkMode)}`}
      >
        <Clock className={`${compact ? "w-2.5 h-2.5" : "w-3 h-3"} flex-shrink-0`} />
        {block.startTime} – {block.endTime}
      </div>
      {!compact && (
        <div
          className="text-xs mt-0.5 font-medium truncate"
          style={{ color: block.athleteColor }}
        >
          {block.athleteName}
        </div>
      )}
    </div>
  );

  // --- Week View ---
  const renderWeekView = () => (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        {/* Day headers */}
        <div className={`grid grid-cols-7 border-b ${getLightBorder(isDarkMode)}`}>
          {weekDates.map((date, i) => {
            const isToday = date.toDateString() === todayStr;
            return (
              <div
                key={i}
                className={`p-2 text-center border-r last:border-r-0 ${getLightBorder(isDarkMode)}`}
              >
                <div
                  className={`text-xs font-semibold uppercase ${getSecondaryText(isDarkMode)}`}
                >
                  {DAY_LABELS[i]}
                </div>
                <div
                  className={`mt-0.5 text-sm font-medium inline-flex items-center justify-center w-7 h-7 rounded-full ${
                    isToday ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900" : getPrimaryText(isDarkMode)
                  }`}
                >
                  {date.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Day columns with schedule blocks */}
        <div className="grid grid-cols-7">
          {weekDates.map((date, i) => {
            const blocks = scheduleByDay[i] || [];
            return (
              <div
                key={i}
                className={`p-1.5 border-r last:border-r-0 min-h-[80px] ${getLightBorder(isDarkMode)}`}
              >
                {blocks.length > 0 ? (
                  <div className="space-y-1.5">
                    {blocks.map((block, j) => (
                      <React.Fragment key={j}>{renderBlock(block)}</React.Fragment>
                    ))}
                  </div>
                ) : (
                  <div
                    className={`text-xs text-center pt-4 ${getMutedText(isDarkMode)}`}
                  >
                    –
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // --- Month View ---
  const renderMonthView = () => (
    <>
      {/* Day name headers (Mon-first) */}
      <div className={`grid grid-cols-7 border-b ${getLightBorder(isDarkMode)}`}>
        {DAY_LABELS.map((d) => (
          <div
            key={d}
            className={`p-2 text-center text-xs font-semibold uppercase ${getSecondaryText(isDarkMode)}`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7">
        {monthDays.map((cell, idx) => {
          const blocks = cell.day !== null ? scheduleByDay[cell.dayOfWeekIdx] || [] : [];
          const isToday = cell.date?.toDateString() === todayStr;
          const isExpanded = expandedDay === cell.day;

          return (
            <div
              key={idx}
              className={`min-h-[70px] sm:min-h-[90px] p-1 border-b border-r ${getLightBorder(isDarkMode)} ${
                cell.day === null
                  ? isDarkMode
                    ? "bg-gray-800/50"
                    : "bg-gray-50"
                  : blocks.length > 0
                    ? "cursor-pointer"
                    : ""
              } ${
                isExpanded
                  ? isDarkMode
                    ? "bg-gray-700/50"
                    : "bg-blue-50/50"
                  : ""
              }`}
              onClick={() => {
                if (cell.day !== null && blocks.length > 0) {
                  setExpandedDay(isExpanded ? null : cell.day);
                }
              }}
            >
              {cell.day !== null && (
                <>
                  <div
                    className={`text-sm font-medium inline-flex items-center justify-center w-6 h-6 rounded-full mb-0.5 ${
                      isToday ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900" : getPrimaryText(isDarkMode)
                    }`}
                  >
                    {cell.day}
                  </div>

                  {/* Dots showing classes */}
                  {blocks.length > 0 && !isExpanded && (
                    <div className="flex flex-wrap gap-0.5 mt-0.5">
                      {blocks.map((block, j) => (
                        <div
                          key={j}
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: block.athleteColor }}
                          title={`${block.programLabel} – ${block.athleteName}`}
                        />
                      ))}
                    </div>
                  )}

                  {/* Expanded detail on click */}
                  {isExpanded && (
                    <div className="space-y-1 mt-1">
                      {blocks.map((block, j) => (
                        <React.Fragment key={j}>
                          {renderBlock(block, true)}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </>
  );

  // --- Athlete legend ---
  const renderLegend = () => (
    <div className="flex flex-wrap gap-3 mt-3 px-1">
      {athletes
        .filter((a) =>
          a.enrollments?.some(
            (e) =>
              e.program_slot?.times &&
              e.program_slot.times.some((t) => t && t !== "x")
          )
        )
        .map((a) => (
          <div key={a.id} className="flex items-center gap-1.5">
            <div
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: athleteColorMap[a.id] }}
            />
            <span className={`text-xs ${getSecondaryText(isDarkMode)}`}>
              {a.first_name} {a.last_name}
            </span>
          </div>
        ))}
    </div>
  );

  return (
    <Card padding="none" className="overflow-hidden">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <h2 className={`text-lg font-semibold ${getPrimaryText(isDarkMode)}`}>
            Class Schedule
          </h2>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle */}
          <div
            className={`flex rounded-lg overflow-hidden border ${getLightBorder(isDarkMode)}`}
          >
            <button
              onClick={() => {
                setViewMode("week");
                setExpandedDay(null);
              }}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === "week"
                  ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900"
                  : isDarkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              Week
            </button>
            <button
              onClick={() => {
                setViewMode("month");
                setExpandedDay(null);
              }}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === "month"
                  ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900"
                  : isDarkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              Month
            </button>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-1">
            <Button
              variant="neutral"
              size="sm"
              iconOnly
              onClick={goToPrevious}
              aria-label={viewMode === "week" ? "Previous week" : "Previous month"}
              icon={<ChevronLeft className="w-4 h-4" />}
            />
            <Button variant="neutral" size="sm" onClick={goToToday}>
              Today
            </Button>
            <Button
              variant="neutral"
              size="sm"
              iconOnly
              onClick={goToNext}
              aria-label={viewMode === "week" ? "Next week" : "Next month"}
              icon={<ChevronRight className="w-4 h-4" />}
            />
          </div>
        </div>
      </div>

      {/* Date label */}
      <div className={`px-4 pb-2 text-sm font-medium ${getSecondaryText(isDarkMode)}`}>
        {viewMode === "week" ? weekRange : monthYear}
      </div>

      {/* Calendar body */}
      <div className={`border-t ${getLightBorder(isDarkMode)}`}>
        {viewMode === "week" ? renderWeekView() : renderMonthView()}
      </div>

      {/* Athlete legend (only if more than 1 athlete) */}
      {athletes.length > 1 && (
        <div className={`px-4 pb-3 border-t ${getLightBorder(isDarkMode)} pt-2`}>
          {renderLegend()}
        </div>
      )}
    </Card>
  );
};

export default MemberScheduleCalendar;
